//@flow
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { BenefitsChartHeader } from './BenefitsChartHeader';
import { BenefitsChartRow, ROW_TYPE_CATEGORY } from './BenefitsChartRow';
import { BenefitsChartCell } from './BenefitsChartCell';
import { childPrograms as childProgramCategories } from './childPrograms';

import styles from './BenefitsChart.scss';

import type { BenefitsChart_tradeFirm as TradeFirmType } from './__generated__/BenefitsChart_tradeFirm.graphql';
import type { BenefitsChart_viewer as ViewerType } from './__generated__/BenefitsChart_viewer.graphql';

type Props = {
    tradeFirm: TradeFirmType,
    viewer: ViewerType,
};

export function BenefitsChartComponent(props: Props) {
    const { tradeFirm, viewer } = props;

    const chartRows = childProgramCategories.reduce((acc, category) => {
        const labelRow = (
            <BenefitsChartRow key={`child-program-${category.id}`} type={ROW_TYPE_CATEGORY}>
                <BenefitsChartCell className={styles.programCategoryCell}>
                    {category.displayLabel}
                </BenefitsChartCell>
                <BenefitsChartCell />
                <BenefitsChartCell />
                <BenefitsChartCell />
                <BenefitsChartCell />
                <BenefitsChartCell />
            </BenefitsChartRow>
        );

        const { childPrograms } = category;

        const childProgramsCount = childPrograms.length;
        const childProgramRows = childPrograms.map((childProgram, i) => {
            const isLastChildProgram = i + 1 === childProgramsCount;
            const programNameCellClass = classnames(styles.programNameCell, {
                [styles.lastChildProgramCell]: isLastChildProgram,
            });

            return (
                <BenefitsChartRow key={`child-program-row-${category.id}-${i}`}>
                    <BenefitsChartCell className={programNameCellClass}>
                        {childProgram.displayName}
                    </BenefitsChartCell>
                    <BenefitsChartCell
                        className={classnames(styles.dataCell, styles.cellMember)}
                        tooltipContent={childProgram.memberInfo}
                    >
                        {childProgram.memberValue}
                    </BenefitsChartCell>
                    <BenefitsChartCell
                        className={classnames(styles.dataCell, styles.cellBronze)}
                        tooltipContent={childProgram.bronzeInfo}
                    >
                        {childProgram.bronzeValue}
                    </BenefitsChartCell>
                    <BenefitsChartCell
                        className={classnames(styles.dataCell, styles.cellSilver)}
                        tooltipContent={childProgram.silverInfo}
                    >
                        {childProgram.silverValue}
                    </BenefitsChartCell>
                    <BenefitsChartCell
                        className={classnames(styles.dataCell, styles.cellGold)}
                        tooltipContent={childProgram.goldInfo}
                    >
                        {childProgram.goldValue}
                    </BenefitsChartCell>
                    <BenefitsChartCell
                        className={classnames(styles.dataCell, styles.cellPlatinum)}
                        tooltipContent={childProgram.platinumInfo}
                    >
                        {childProgram.platinumValue}
                    </BenefitsChartCell>
                </BenefitsChartRow>
            );
        });

        return [...acc, labelRow, ...childProgramRows];
    }, []);

    return (
        <div className={styles.container}>
            <BenefitsChartHeader tradeFirm={(tradeFirm: Object)} viewer={(viewer: Object)} />
            {chartRows}
            <div className={styles.editorialQualification}>
                <FormattedMessage
                    id="abt.rewards.BenefitsChart.editorialQualification"
                    defaultMessage="*Only firms with published profiles are eligible. Inclusion is subject to 1stdibs' discretion."
                />
            </div>
        </div>
    );
}

export const BenefitsChart = createFragmentContainer(BenefitsChartComponent, {
    tradeFirm: graphql`
        fragment BenefitsChart_tradeFirm on TradeFirmType {
            ...BenefitsChartHeader_tradeFirm
        }
    `,
    viewer: graphql`
        fragment BenefitsChart_viewer on Viewer {
            ...BenefitsChartHeader_viewer
        }
    `,
});
